import { useCallback, useMemo, useState } from 'react';
import { getWorkTypesAndFields } from '../../services/index.tsx';
import type { WorkTypeToFieldsMap, UseLazyWorkTypeFieldsArguments } from '../../services/types.tsx';
import type { WorkTypeFieldsUtil } from './types.tsx';
/**
 * This hook is responsible for fetching the issue types and its associated fields for a given project key.
 * It exposes a function to load the data on demand (lazily) and a function to check if a specific issue type has a specific field.
 * */
export const useLazyWorkTypesConfigMap = ({
	projectKey,
}: UseLazyWorkTypeFieldsArguments): WorkTypeFieldsUtil => {
	// This state is kept per individual ICC trigger (each footer "+ Create" and sibling "+" (hidden) button)
	// Use shared state instead of local state for "dirty" to avoid re-fetching between cards
	const [dirty, setDirty] = useState(false);
	const [workTypeMap, setWorkTypeMap] = useState<WorkTypeToFieldsMap | null>(null);
	const [error, setError] = useState<Error | null>(null);
	const loading = dirty && !workTypeMap && !error;
	const done = dirty && !loading;

	const load = useCallback(() => {
		// define caching strategy here. For now there's none and it won't be called twice for each button
		if (dirty || !projectKey) return;
		setDirty(true);
		getWorkTypesAndFields({ projectKey }).then(setWorkTypeMap).catch(setError);
	}, [dirty, projectKey]);

	const checkWorkTypeHasField = useCallback(
		(workTypeId: string | number | null, fieldId: string): boolean => {
			if (!workTypeId || !workTypeMap || !workTypeMap[workTypeId]) return false;
			return workTypeMap[workTypeId].fields.some((f) => f.fieldId === fieldId);
		},
		[workTypeMap],
	);

	const getFieldConfigurationForWorkType = useCallback(
		<Config extends Record<string, unknown>>(
			workTypeId: string | number | null,
			fieldId: string,
		) => {
			if (!workTypeId || !workTypeMap || !workTypeMap[workTypeId]) return null;
			const fields = workTypeMap?.[workTypeId]?.fields ?? [];
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return (fields.find((f: { fieldId: string }) => f.fieldId === fieldId) as Config) ?? null;
		},
		[workTypeMap],
	);

	return useMemo(
		() => ({
			load,
			error,
			done,
			loading,
			workTypeMap,
			checkWorkTypeHasField,
			getFieldConfigurationForWorkType,
		}),
		[
			load,
			error,
			done,
			loading,
			workTypeMap,
			checkWorkTypeHasField,
			getFieldConfigurationForWorkType,
		],
	);
};
